/**
 * The audit-log filter bar's state, and how it becomes a query (f1.5).
 *
 * Four filters reach the SERVER -- date range, actor, event family and outcome
 * (BR-F1.5-7) -- and two more are purely presentational: which clock the
 * timestamps render in, and a contains-search over the entries already loaded.
 *
 * The entity-type / entity-id pair the screen used to offer is GONE. It asked
 * the reader to know an internal type name and a full UUID before it narrowed
 * anything, which is a lookup rather than a filter. The entity columns are
 * still on the table, and the contains-search below now reaches them by
 * typing.
 *
 * The actor filter is a SET, chosen from a list the SERVER builds out of the
 * log itself. It is not a user directory, and could not be: no directory
 * lists a background job, and none lists a failed sign-in whose presented
 * identity matched nobody, which is exactly the row an investigation is
 * looking for (design D5). Several actors combine by OR on the server, so
 * "who, between these three people, touched this last week" is one query.
 *
 * `text` is deliberately NOT sent to the server. A substring scan across every
 * column of the largest table in the system has no index that can serve it and
 * would be an outage rather than a filter (design D2). It narrows the rows
 * ALREADY FETCHED instead -- the set the reader is looking at -- and the
 * screen says so next to the box.
 */

import type { AuditEventFamily, AuditOutcome } from '@/api/client';
import type { AuditLogFilterQuery } from '@/api/queries';

import {
  addDays,
  instantRange,
  isLocalDay,
  todayLocal,
  type LocalDay,
  type TimeZoneMode,
} from './auditTime';

/** How far back the screen looks by default. The screen always sends a range. */
export const DEFAULT_WINDOW_DAYS = 7;

/**
 * The windows the date control offers as one click.
 *
 * Three, not a scale: they are the shapes of the three questions this screen
 * is actually opened for -- "what happened this week", "since the last
 * fortnightly review", "over the past month or so".
 */
export const RANGE_PRESET_DAYS = [7, 15, 40] as const;

export type RangePresetDays = (typeof RANGE_PRESET_DAYS)[number];

/** An actor chosen from the list: by account when it has one, by snapshot when not. */
export type ActorFilter =
  | { kind: 'user'; userId: string; label: string }
  | { kind: 'display'; display: string };

/** A stable identity for one chosen actor, for keys and set membership. */
export function actorKey(actor: ActorFilter): string {
  return actor.kind === 'user' ? `user:${actor.userId}` : `display:${actor.display}`;
}

/** Add an actor to the selection, or remove it if it is already chosen. */
export function toggleActor(
  actors: readonly ActorFilter[],
  actor: ActorFilter,
): ActorFilter[] {
  const key = actorKey(actor);
  const without = actors.filter((chosen) => actorKey(chosen) !== key);
  return without.length === actors.length ? [...actors, actor] : without;
}

export interface AuditLogFilterState {
  fromDay: LocalDay;
  /** INCLUSIVE last day, as the reader thinks of it. */
  toDay: LocalDay;
  /** Empty means every actor. Several actors combine by OR on the server. */
  actors: readonly ActorFilter[];
  eventFamily: AuditEventFamily | '';
  outcome: AuditOutcome | '';
  /** Which clock every timestamp renders in. Display only; never sent. */
  timeZone: TimeZoneMode;
  /** Contains-search across the loaded rows. Display only; never sent. */
  text: string;
}

/** The range a preset stands for, ending today. */
export function presetRange(
  days: number,
  now: Date = new Date(),
): { fromDay: LocalDay; toDay: LocalDay } {
  const today = todayLocal(now);
  return { fromDay: addDays(today, -days), toDay: today };
}

export function defaultFilters(now: Date = new Date()): AuditLogFilterState {
  return {
    ...presetRange(DEFAULT_WINDOW_DAYS, now),
    actors: [],
    eventFamily: '',
    outcome: '',
    timeZone: 'local',
    text: '',
  };
}

/**
 * Which preset the current range IS, or null when it is a hand-picked range.
 *
 * Derived rather than stored, so the control cannot claim "last 7 days" while
 * the inputs hold something else: there is one source of truth for the range,
 * and it is the two days.
 */
export function activePreset(
  state: Pick<AuditLogFilterState, 'fromDay' | 'toDay'>,
  now: Date = new Date(),
): RangePresetDays | null {
  for (const days of RANGE_PRESET_DAYS) {
    const range = presetRange(days, now);
    if (range.fromDay === state.fromDay && range.toDay === state.toDay) return days;
  }
  return null;
}

/** Why the current filters cannot be sent, or `null` when they can. */
export function filterProblem(state: AuditLogFilterState): string | null {
  if (!isLocalDay(state.fromDay) || !isLocalDay(state.toDay)) {
    return 'Choose both a start and an end date.';
  }
  if (state.fromDay > state.toDay) {
    return 'The start date is after the end date.';
  }
  return null;
}

/**
 * The server query for a filter state. Only called when `filterProblem` is
 * null, so every value sent is one the server will accept.
 *
 * `timeZone` and `text` are absent BY DESIGN: neither narrows what the server
 * returns, and putting either in the query key would refetch the whole list on
 * every keystroke and every toggle.
 */
export function toQuery(state: AuditLogFilterState): AuditLogFilterQuery {
  const range = instantRange(state.fromDay, state.toDay);

  return {
    occurred_from: range.from,
    occurred_to: range.to,
    actor_user_id: state.actors.flatMap((actor) =>
      actor.kind === 'user' ? [actor.userId] : [],
    ),
    actor_display: state.actors.flatMap((actor) =>
      actor.kind === 'display' ? [actor.display] : [],
    ),
    event_family: state.eventFamily || null,
    outcome: state.outcome || null,
  };
}

/** Whether anything differs from the default window and no other filter. */
export function isFiltered(state: AuditLogFilterState, defaults: AuditLogFilterState): boolean {
  return (
    state.fromDay !== defaults.fromDay ||
    state.toDay !== defaults.toDay ||
    state.actors.length > 0 ||
    state.eventFamily !== '' ||
    state.outcome !== '' ||
    state.timeZone !== defaults.timeZone ||
    state.text.trim() !== ''
  );
}

/** The actor filter a row implies: its account id if it has one, else its snapshot. */
export function actorFilterFor(entry: {
  actor_user_id: string | null;
  actor_display: string;
}): ActorFilter {
  return entry.actor_user_id
    ? { kind: 'user', userId: entry.actor_user_id, label: entry.actor_display }
    : { kind: 'display', display: entry.actor_display };
}

export function actorLabel(actor: ActorFilter): string {
  return actor.kind === 'user' ? actor.label : actor.display;
}
